import { useEffect, useMemo, useState } from "react";
import supabase from "../../../../../utils/supabase";

/**
 * 汎用データ取得フック（詳細ログ + エラー返却版）
 * - plugin.fetch があればそれを優先
 * - 無ければ PostgREST で select / or / order / range
 * - 1行目のキーを必ずログ出力（列欠落の検知）
 */
export default function useConsoleData({
  plugin,            // { table, select, normalize, defaultSort, or, requiredFields?, debugTag?, fetch? }
  page = 0,
  pageSize = 25,
  sortModel = [],
  search = "",
  searchOptions = {},
  debug = false,
  refreshKey = 0,
}) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);

  const { orderBy, ascending } = useMemo(() => {
    if (Array.isArray(sortModel) && sortModel.length > 0) {
      const s = sortModel[0];
      return { orderBy: s.field, ascending: s.sort !== "desc" };
    }
    const def = plugin?.defaultSort ?? { field: "created_at", sort: "desc" };
    return { orderBy: def.field, ascending: def.sort !== "desc" };
  }, [sortModel, plugin]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      if (!plugin) return;
      setLoading(true);
      setError(null);

      const tag = plugin?.debugTag || plugin?.table || "Console";
      const from = page * pageSize;
      const to = from + pageSize - 1;

      try {
        // 1) plugin.fetch 優先
        if (typeof plugin.fetch === "function") {
          if (debug) console.groupCollapsed(`[${tag}] plugin.fetch`);
          if (debug) console.log({ page, pageSize, orderBy, ascending, search, searchOptions });

          const res = (await plugin.fetch({ page, pageSize, orderBy, ascending, search, searchOptions })) || {};
          const fetchedRows = Array.isArray(res.rows) ? res.rows : [];
          const fetchedTotal = typeof res.total === "number" ? res.total : fetchedRows.length;

          if (debug) {
            const first = fetchedRows[0] ?? {};
            console.log(`keys(1st):`, Object.keys(first));
            console.log(`rows=${fetchedRows.length} total=${fetchedTotal}`);
            console.groupEnd();
          }

          if (!isMounted) return;
          setRows(fetchedRows);
          setTotal(fetchedTotal);
          return;
        }

        // 2) PostgREST 経由
        const selectStr = plugin?.select || "*";
        let query = supabase
          .from(plugin.table)
          .select(selectStr, { count: "exact" })
          .range(from, to);

        const orExpr = typeof plugin?.or === "function" ? plugin.or(search) : undefined;
        if (orExpr) query = query.or(orExpr);

        try {
          query = query.order(orderBy, { ascending });
        } catch {
          query = query.order("created_at", { ascending: false });
        }

        if (debug) {
          console.groupCollapsed(`[${tag}] default fetch`);
          console.log({ table: plugin.table, from, to, orderBy, ascending, search, select: selectStr, or: orExpr });
        }

        const { data, error: qerr, count } = await query;
        if (qerr) {
          if (debug) console.error("default fetch error", qerr);
          throw qerr;
        }

        if (debug) {
          const first = (data && data[0]) || {};
          const keys = Object.keys(first || {});
          console.log(`keys(1st):`, keys);
          if (Array.isArray(plugin?.requiredFields) && plugin.requiredFields.length > 0) {
            const missing = plugin.requiredFields.filter((k) => !keys.includes(k));
            if (missing.length > 0) console.warn(`MISSING fields:`, missing);
          }
          console.log(`rows=${(data || []).length} total(count)=${count}`);
          console.groupEnd();
        }

        let mapped = Array.isArray(data) ? data : [];
        if (typeof plugin?.normalize === "function") {
          try {
            mapped = mapped.map(plugin.normalize);
          } catch (normErr) {
            if (debug) console.error("normalize error", normErr);
          }
        }

        if (!isMounted) return;
        setRows(mapped);
        setTotal(typeof count === "number" ? count : mapped.length);
      } catch (err) {
        if (debug) console.error(`[${plugin?.debugTag || plugin?.table || "Console"}] fatal`, err);
        if (!isMounted) return;
        setRows([]);
        setTotal(0);
        setError(err);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => { isMounted = false; };
  }, [plugin, page, pageSize, orderBy, ascending, search, debug, refreshKey]);

  return { rows, total, loading, error };
}
